// Entegrasyonlar, API, ekip, roller, denetim (C74–C79): Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Gizli değerler (entegrasyon parolaları, API anahtarı, webhook imza anahtarı) yalnız oluşturulduğu yanıtta bir kez döner.
import { z } from 'zod';
import type { Module, TenantType } from '../enums';
import {
  ACTIONS,
  FLAG_GATES,
  PERMISSION_MODULES,
  type Action,
  type PermissionMatrix,
  type PermissionModule,
} from '../permissions';
import { IsoDate, PermissionMatrixSchema, Phone } from './common';
import { PLATFORM_AUDIT_ACTION_LABELS, PLATFORM_AUDIT_ENTITY_LABELS } from './admin-platform';

// ───────── entegrasyonlar (C74) ─────────

export const INTEGRATION_TYPES = ['HR', 'SCHOOL_SIS', 'ACCOUNTING', 'ERP', 'GPS', 'SMS', 'MAP', 'CALENDAR'] as const;
export type IntegrationType = (typeof INTEGRATION_TYPES)[number];

export const INTEGRATION_STATUSES = ['CONNECTED', 'DISCONNECTED', 'ERROR'] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];
export const INTEGRATION_STATUS_LABELS: Record<IntegrationStatus, string> = {
  CONNECTED: 'Bağlı',
  DISCONNECTED: 'Bağlı değil',
  ERROR: 'Hata',
};

export interface IntegrationFieldSpec {
  key: string;
  label: string;
  /** gizli alan: sunucuda şifreli saklanır, yanıtta yalnız maskeli görünür */
  secret?: boolean;
  required?: boolean;
  placeholder?: string;
  hint?: string;
}

export interface IntegrationSpec {
  title: string;
  description: string;
  /** "Şimdi eşitle" neyi eşitler */
  syncs: string;
  providers: string[];
  fields: IntegrationFieldSpec[];
  /** özellik bayrağı kapalıysa kart hiç görünmez */
  flag?: Module;
}

export const INTEGRATION_CATALOG: Record<IntegrationType, IntegrationSpec> = {
  HR: {
    title: 'İK sistemi',
    description: 'Personel listesini İK sisteminden alın; işe giren ve ayrılan çalışanlar yolcu listesine yansısın.',
    syncs: 'personel kaydı',
    providers: ['Kolay İK', 'BordroPlus', 'Workday', 'SAP SuccessFactors'],
    fields: [
      { key: 'tenantUrl', label: 'Hesap adresi', placeholder: 'https://firma.kolayik.com', required: true },
      { key: 'apiKey', label: 'API anahtarı', secret: true, required: true },
    ],
  },
  SCHOOL_SIS: {
    title: 'Okul bilgi sistemi',
    description: 'Öğrenci, sınıf ve veli bilgilerini okul bilgi sisteminden eşitleyin.',
    syncs: 'öğrenci kaydı',
    providers: ['K12Net', 'Okulsis', 'Mebbis aktarımı'],
    fields: [
      { key: 'schoolCode', label: 'Kurum kodu', placeholder: '123456', required: true },
      { key: 'apiKey', label: 'Erişim anahtarı', secret: true, required: true },
    ],
    flag: 'SCHOOL',
  },
  ACCOUNTING: {
    title: 'Muhasebe',
    description: 'Kesilen faturaları ve tahsilatları muhasebe yazılımına aktarın.',
    syncs: 'fatura',
    providers: ['Logo Tiger', 'Mikro', 'Paraşüt', 'Luca'],
    fields: [
      { key: 'firmNo', label: 'Firma numarası', placeholder: '001', required: true },
      { key: 'username', label: 'Kullanıcı adı', required: true },
      { key: 'password', label: 'Parola', secret: true, required: true },
    ],
  },
  ERP: {
    title: 'ERP',
    description: 'Maliyet merkezleri ve sözleşmeleri ERP ile eşitleyin.',
    syncs: 'sözleşme',
    providers: ['SAP Business One', 'Netsis', 'Microsoft Dynamics'],
    fields: [
      { key: 'serverUrl', label: 'Sunucu adresi', placeholder: 'https://erp.firma.com.tr', required: true },
      { key: 'companyDb', label: 'Şirket veritabanı', required: true },
      { key: 'password', label: 'Servis parolası', secret: true, required: true },
    ],
  },
  GPS: {
    title: 'Araç takip (GPS)',
    description: 'Araçlardaki takip cihazlarının konumunu SEROTA haritasına aktarın.',
    syncs: 'araç',
    providers: ['Arvento', 'Mobiliz', 'Trio Mobil'],
    fields: [
      { key: 'username', label: 'Kullanıcı adı', required: true },
      { key: 'apiKey', label: 'API anahtarı', secret: true, required: true },
    ],
  },
  SMS: {
    title: 'SMS sağlayıcısı',
    description: 'Duyuru ve acil durum SMS’lerini kendi SMS hesabınızdan, kendi başlığınızla gönderin.',
    syncs: 'SMS bakiyesi',
    providers: ['Netgsm', 'İleti Merkezi', 'Verimor'],
    fields: [
      { key: 'username', label: 'Kullanıcı adı', required: true },
      { key: 'password', label: 'Parola', secret: true, required: true },
      { key: 'header', label: 'SMS başlığı', placeholder: 'ABCTURIZM', required: true, hint: 'Onaylı başlığınız.' },
    ],
  },
  MAP: {
    title: 'Harita sağlayıcısı',
    description: 'Varsayılan SEROTA haritası yerine kendi harita ve rota hesabınızı kullanın.',
    syncs: 'kota bilgisi',
    providers: ['SEROTA haritası (varsayılan)', 'Google Maps Platform'],
    fields: [{ key: 'apiKey', label: 'API anahtarı', secret: true, hint: 'Varsayılan harita için boş bırakın.' }],
  },
  CALENDAR: {
    title: 'Takvim',
    description: 'Resmî tatilleri ve kurum takvimini (tatil, yarım gün) sefer planına yansıtın.',
    syncs: 'takvim günü',
    providers: ['Google Takvim', 'Microsoft 365'],
    fields: [
      { key: 'calendarId', label: 'Takvim kimliği', placeholder: 'kurum@firma.com.tr', required: true },
      { key: 'token', label: 'Erişim belirteci', secret: true, required: true },
    ],
  },
};

export interface IntegrationSyncResult {
  ok: boolean;
  records: number;
  message: string;
  at: string;
}

export interface IntegrationItem {
  /** kayıt yoksa null (henüz hiç bağlanmamış) */
  id: string | null;
  type: IntegrationType;
  provider: string | null;
  status: IntegrationStatus;
  lastSyncAt: string | null;
  /** gizli olmayan alan değerleri */
  values: Record<string, string>;
  /** gizli alanlar: yalnız maskeli ("••••3f9a"); hiç girilmemişse anahtar yok */
  secrets: Record<string, string>;
  lastResult: IntegrationSyncResult | null;
}

export const IntegrationTypeParam = z.object({ type: z.enum(INTEGRATION_TYPES) });

export const IntegrationConnectInput = z.object({
  provider: z.string().trim().min(1, 'Sağlayıcı seçin.').max(80),
  /** gizli alan boş gönderilirse kayıtlı değer korunur */
  values: z.record(z.string().max(80), z.string().trim().max(500)).default({}),
});
export type IntegrationConnectInput = z.infer<typeof IntegrationConnectInput>;

// ───────── API (C75) ─────────

export const API_SCOPES = [
  'trips:read',
  'live:read',
  'routes:read',
  'passengers:read',
  'passengers:write',
  'vehicles:read',
  'invoices:read',
  'reports:read',
] as const;
export type ApiScope = (typeof API_SCOPES)[number];
export const API_SCOPE_LABELS: Record<ApiScope, string> = {
  'trips:read': 'Seferleri okuma',
  'live:read': 'Canlı konum okuma',
  'routes:read': 'Rotaları okuma',
  'passengers:read': 'Yolcu listesini okuma',
  'passengers:write': 'Yolcu ekleme ve güncelleme',
  'vehicles:read': 'Araçları okuma',
  'invoices:read': 'Faturaları okuma',
  'reports:read': 'Raporları okuma',
};

export const ApiKeyCreateInput = z.object({
  name: z.string().trim().min(2, 'Anahtara bir ad verin (en az 2 karakter).').max(60),
  scopes: z.array(z.enum(API_SCOPES)).min(1, 'En az bir yetki seçin.'),
});
export type ApiKeyCreateInput = z.infer<typeof ApiKeyCreateInput>;

export interface ApiKeyItem {
  id: string;
  name: string;
  prefix: string;
  /** anahtarın son 4 karakteri (eski kayıtlarda yok) */
  last4: string | null;
  scopes: string[];
  createdAt: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/** Oluşturma yanıtı: tam anahtar YALNIZ burada, bir kez döner. */
export interface ApiKeyCreated {
  key: ApiKeyItem;
  secret: string;
}

export const WEBHOOK_EVENTS = [
  'trip.started',
  'trip.completed',
  'trip.delayed',
  'trip.cancelled',
  'alert.created',
  'alert.resolved',
  'request.created',
  'complaint.created',
  'document.expiring',
  'invoice.issued',
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];
export const WEBHOOK_EVENT_LABELS: Record<WebhookEvent | 'test.ping', string> = {
  'trip.started': 'Sefer başladı',
  'trip.completed': 'Sefer tamamlandı',
  'trip.delayed': 'Sefer gecikti',
  'trip.cancelled': 'Sefer iptal edildi',
  'alert.created': 'Uyarı oluştu',
  'alert.resolved': 'Uyarı çözüldü',
  'request.created': 'Yeni talep',
  'complaint.created': 'Yeni şikayet',
  'document.expiring': 'Belge süresi doluyor',
  'invoice.issued': 'Fatura kesildi',
  'test.ping': 'Test olayı',
};

const PRIVATE_HOST =
  /^(localhost|.*\.local|.*\.internal|127\.\d+\.\d+\.\d+|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+|169\.254\.\d+\.\d+|0\.0\.0\.0|\[?::1\]?)$/i;

export const WebhookUrl = z
  .string()
  .trim()
  .max(500, 'Adres en fazla 500 karakter olabilir.')
  .url('Geçerli bir adres girin (https://…).')
  .refine((u) => u.startsWith('https://'), 'Webhook adresi https:// ile başlamalı.')
  .refine((u) => {
    try {
      return !PRIVATE_HOST.test(new URL(u).hostname);
    } catch {
      return false;
    }
  }, 'İç ağ veya yerel adreslere webhook gönderilemez.');

export const WebhookInput = z.object({
  url: WebhookUrl,
  events: z.array(z.enum(WEBHOOK_EVENTS)).min(1, 'En az bir olay seçin.'),
  active: z.boolean().default(true),
});
export type WebhookInput = z.infer<typeof WebhookInput>;

export const WebhookUpdateInput = z
  .object({
    url: WebhookUrl.optional(),
    events: z.array(z.enum(WEBHOOK_EVENTS)).min(1, 'En az bir olay seçin.').optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => v.url !== undefined || v.events !== undefined || v.active !== undefined, 'Değişiklik yok.');
export type WebhookUpdateInput = z.infer<typeof WebhookUpdateInput>;

export const WEBHOOK_DELIVERY_STATUSES = ['SUCCESS', 'FAILED', 'RETRYING'] as const;
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUSES)[number];
export const WEBHOOK_DELIVERY_STATUS_LABELS: Record<WebhookDeliveryStatus, string> = {
  SUCCESS: 'Başarılı',
  FAILED: 'Başarısız',
  RETRYING: 'Yeniden deneniyor',
};

export interface WebhookItem {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  /** "whsec_••••a1b2" */
  secretMasked: string;
  createdAt: string;
  lastDelivery: { status: WebhookDeliveryStatus; responseCode: number | null; at: string } | null;
  failed24h: number;
}

/** Oluşturma / anahtar yenileme yanıtı: imza anahtarı YALNIZ burada, bir kez döner. */
export interface WebhookCreated {
  webhook: WebhookItem;
  secret: string;
}

export interface WebhookDeliveryItem {
  id: string;
  webhookId: string;
  url: string;
  event: string;
  status: WebhookDeliveryStatus;
  responseCode: number | null;
  attempt: number;
  durationMs: number | null;
  createdAt: string;
  payload: unknown;
}

export const WebhookDeliveryQuery = z.object({
  webhookId: z.string().max(64).optional(),
  status: z.enum(WEBHOOK_DELIVERY_STATUSES).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

export interface ApiUsage {
  /** aylık API çağrısı (UsageMeter API_CALLS), eskiden yeniye */
  months: { period: string; calls: number }[];
  currentPeriod: string;
  currentCalls: number;
  previousCalls: number | null;
  activeKeys: number;
  deliveries7d: number;
  /** son 7 gün webhook teslim başarısı (%); teslim yoksa null */
  successRate7d: number | null;
}

// ───────── ekip (C76–C77) ─────────

export const MEMBER_STATUSES = ['ACTIVE', 'INVITED', 'DISABLED'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];
export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  ACTIVE: 'Aktif',
  INVITED: 'Davet bekliyor',
  DISABLED: 'Askıda',
};

/** Kiracı tipine göre "sahip" rolü: son sahip silinemez, askıya alınamaz, rolü düşürülemez. */
export const ADMIN_ROLE_KEY: Record<TenantType, string> = {
  SERVICE_COMPANY: 'OWNER',
  INSTITUTION: 'INST_ADMIN',
  PLATFORM: 'PLATFORM_ADMIN',
};

export interface TeamMemberItem {
  /** üyelik kimliği */
  id: string;
  userId: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  /** KVKK: liste maskeli telefon gösterir */
  phoneMasked: string;
  title: string | null;
  roleId: string;
  roleKey: string;
  roleName: string;
  customRole: boolean;
  status: MemberStatus;
  lastLoginAt: string | null;
  invitedAt: string;
  isSelf: boolean;
  isAdmin: boolean;
  /** silme / askıya alma / rol düşürme engeli (ör. son sahip, kendiniz) */
  lockReason: string | null;
}

export interface TeamList {
  items: TeamMemberItem[];
  counts: { active: number; invited: number; disabled: number };
}

const Name = (label: string) =>
  z.string().trim().min(2, `${label} en az 2 karakter olmalı.`).max(60, `${label} en fazla 60 karakter olabilir.`);

export const TeamInviteInput = z.object({
  phone: Phone,
  firstName: Name('Ad'),
  lastName: Name('Soyad'),
  roleId: z.string().min(1, 'Rol seçin.').max(64),
  title: z.string().trim().max(80).optional(),
});
export type TeamInviteInput = z.infer<typeof TeamInviteInput>;

export const TeamMemberUpdateInput = z
  .object({
    roleId: z.string().min(1).max(64).optional(),
    title: z.string().trim().max(80).nullable().optional(),
    status: z.enum(['ACTIVE', 'DISABLED']).optional(),
  })
  .refine((v) => v.roleId !== undefined || v.title !== undefined || v.status !== undefined, 'Değişiklik yok.');
export type TeamMemberUpdateInput = z.infer<typeof TeamMemberUpdateInput>;

// ───────── roller ve izinler (C78) ─────────

/** Rol matrisinde kiracı tipine göre düzenlenebilir modüller (platform modülleri hiçbir zaman). */
export const TENANT_ROLE_MODULES: Record<'SERVICE_COMPANY' | 'INSTITUTION', PermissionModule[]> = {
  SERVICE_COMPANY: [
    'operations',
    'trips',
    'routes',
    'optimization',
    'requests',
    'passengers',
    'people',
    'fleet',
    'customers',
    'proposals',
    'announcements',
    'quality',
    'finance',
    'reports',
    'compliance',
    'devices',
    'integrations',
    'team',
    'billing',
    'settings',
    'audit',
  ],
  INSTITUTION: [
    'operations',
    'trips',
    'routes',
    'requests',
    'passengers',
    'proposals',
    'announcements',
    'quality',
    'finance',
    'reports',
    'compliance',
    'team',
    'settings',
    'audit',
  ],
};

export function roleModulesFor(tenantType: TenantType): PermissionModule[] {
  return tenantType === 'PLATFORM' ? [] : TENANT_ROLE_MODULES[tenantType];
}

/** Bayrağı kapalı modül rol matrisinde gizlenir (permissions.ts FLAG_GATES). */
export function roleModuleGate(m: PermissionModule): Module | undefined {
  return FLAG_GATES[m];
}

/** Eylemleri sabit sıraya dizer; düzenle/onayla/dışa aktar varsa görüntüle de eklenir; boş modül düşer. */
export function normalizeMatrix(m: PermissionMatrix): PermissionMatrix {
  const out: PermissionMatrix = {};
  for (const mod of PERMISSION_MODULES) {
    const acts = new Set<Action>(m[mod] ?? []);
    if (!acts.size) continue;
    acts.add('view');
    out[mod] = ACTIONS.filter((a) => acts.has(a));
  }
  return out;
}

export const SYSTEM_ROLE_DESCRIPTIONS: Record<string, string> = {
  OWNER: 'Tüm modüller; ekip, plan ve faturalama dahil.',
  OPERATIONS: 'Canlı operasyon, seferler ve müdahale; diğer modülleri görür.',
  PLANNER: 'Rotalar, optimizasyon, talepler ve teklifler.',
  FLEET: 'Araçlar, belgeler, bakım ve cihazlar.',
  FINANCE: 'Finans, plan ve kullanım; raporları dışa aktarır.',
  SUPPORT: 'Yolcu talepleri, şikayetler ve duyurular.',
  FIELD: 'Sahada canlı operasyonu ve seferleri görür.',
  INST_ADMIN: 'Kurumun yolcuları, talepleri ve ekibi.',
  INST_VIEWER: 'Kurum verilerini yalnız görüntüler.',
};

export interface RoleItem {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
  description: string | null;
  permissions: PermissionMatrix;
  memberCount: number;
}

export interface RolesResponse {
  roles: RoleItem[];
  /** matriste gösterilecek modüller (bayrağı kapalı olanlar hariç) */
  modules: PermissionModule[];
  /** bayrak kapalı olduğu için gizlenen modüller */
  hiddenModules: { module: PermissionModule; flag: Module }[];
  adminRoleKey: string;
}

export const RoleInput = z.object({
  name: z.string().trim().min(2, 'Rol adı en az 2 karakter olmalı.').max(60, 'Rol adı en fazla 60 karakter olabilir.'),
  permissions: PermissionMatrixSchema,
});
export type RoleInput = z.infer<typeof RoleInput>;

// ───────── denetim günlüğü (C79) ─────────

export const AuditLogQuery = z.object({
  actorId: z.string().max(64).optional(),
  action: z.string().max(80).optional(),
  entity: z.string().max(40).optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type AuditLogQuery = z.infer<typeof AuditLogQuery>;

export interface AuditLogRow {
  id: string;
  at: string;
  actorId: string | null;
  /** null: sistem (arka plan işi) */
  actorName: string | null;
  /** SEROTA desteği kimliğe bürünerek yaptıysa */
  impersonated: boolean;
  action: string;
  entity: string;
  entityId: string | null;
  hasChanges: boolean;
}

export interface AuditLogDetail extends AuditLogRow {
  before: unknown;
  after: unknown;
  ip: string | null;
}

export interface AuditFacets {
  actors: { id: string; name: string }[];
  actions: string[];
  entities: string[];
}

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  'trip.intervention.change_vehicle': 'Yedek araç atandı',
  'trip.intervention.change_driver': 'Şoför değiştirildi',
  'trip.intervention.change_guide': 'Rehber değiştirildi',
  'trip.intervention.update_route': 'Durak atlandı',
  'trip.intervention.notify': 'Yolculara bildirim gönderildi',
  'trip.intervention.cancel': 'Sefer iptal edildi',
  'trip.start': 'Sefer başlatıldı',
  'trip.end': 'Sefer bitirildi',
  'trip.incident': 'Olay bildirildi',
  'trip.vehicle_check': 'Araç kontrolü yapıldı',
  'trip.rating': 'Sefer puanlandı',
  'schedule.create': 'Tarife satırı eklendi',
  'schedule.update': 'Tarife güncellendi',
  'schedule.delete': 'Tarife satırı kaldırıldı',
  'alert.ack': 'Uyarı üstlenildi',
  'alert.resolved': 'Uyarı çözüldü',
  'alert.open': 'Uyarı yeniden açıldı',
  'flag.update': 'Modül ayarı değiştirildi',
  'call.masked': 'Maskeli arama yapıldı',
  'complaint.create': 'Şikayet oluşturuldu',
  'request.create': 'Talep oluşturuldu',
  'match.request': 'Hatta katılım talebi',
  'document.upload': 'Belge yüklendi',
  'driver.profile.update': 'Şoför profili güncellendi',
  'user.profile.update': 'Profil güncellendi',
  'user.notification_prefs': 'Bildirim tercihleri değişti',
  'kvkk.request': 'KVKK talebi oluşturuldu',
  'passenger.absence': '“Bugün binmeyeceğim” bildirildi',
  'passenger.weekly_plan': 'Haftalık plan güncellendi',
  'passenger.reservation': 'Başka hat rezervasyonu',
  'passenger.boarding_pass': 'Biniş kartı görüntülendi',
  'support.ticket.create': 'Destek talebi açıldı',
  'integration.connect': 'Entegrasyon bağlandı',
  'integration.configure': 'Entegrasyon ayarları değişti',
  'integration.disconnect': 'Entegrasyon bağlantısı kesildi',
  'integration.sync': 'Entegrasyon eşitlendi',
  'api_key.create': 'API anahtarı oluşturuldu',
  'api_key.revoke': 'API anahtarı iptal edildi',
  'webhook.create': 'Webhook eklendi',
  'webhook.update': 'Webhook güncellendi',
  'webhook.delete': 'Webhook silindi',
  'webhook.rotate_secret': 'Webhook imza anahtarı yenilendi',
  'webhook.test': 'Webhook test olayı gönderildi',
  'team.member.invite': 'Ekibe davet edildi',
  'team.member.role': 'Üyenin rolü değişti',
  'team.member.update': 'Üye bilgisi güncellendi',
  'team.member.suspend': 'Üye askıya alındı',
  'team.member.activate': 'Üye yeniden etkinleştirildi',
  'team.member.remove': 'Üye ekipten çıkarıldı',
  'team.invite.resend': 'Davet SMS’i yeniden gönderildi',
  'role.create': 'Özel rol oluşturuldu',
  'role.update': 'Rol izinleri değişti',
  'role.delete': 'Özel rol silindi',
  'audit.export': 'Denetim günlüğü dışa aktarıldı',
};

export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  Trip: 'Sefer',
  Alert: 'Uyarı',
  Vehicle: 'Araç',
  Driver: 'Şoför',
  Guide: 'Rehber',
  Route: 'Rota',
  Schedule: 'Tarife',
  RouteVersion: 'Rota versiyonu',
  Passenger: 'Yolcu',
  PassengerGroup: 'Yolcu grubu',
  Document: 'Belge',
  MaintenanceRecord: 'Bakım kaydı',
  Company: 'Firma',
  Contract: 'Sözleşme',
  Announcement: 'Duyuru',
  Complaint: 'Şikayet',
  Request: 'Talep',
  Rating: 'Puan',
  Invoice: 'Fatura',
  User: 'Kullanıcı',
  Membership: 'Ekip üyesi',
  Role: 'Rol',
  ApiKey: 'API anahtarı',
  Webhook: 'Webhook (olay bildirimi)',
  Integration: 'Entegrasyon',
  FeatureFlag: 'Modül',
  KvkkRequest: 'KVKK talebi',
  SupportTicket: 'Destek talebi',
  Device: 'Cihaz',
  QrSticker: 'QR etiketi',
  AuditLog: 'Denetim günlüğü',
  CompanySettings: 'Firma ayarları',
};

const VERB_LABELS: Record<string, string> = {
  create: 'oluşturuldu',
  update: 'güncellendi',
  delete: 'silindi',
  remove: 'kaldırıldı',
  approve: 'onaylandı',
  reject: 'reddedildi',
  publish: 'yayınlandı',
  archive: 'arşivlendi',
  cancel: 'iptal edildi',
  assign: 'atandı',
  upload: 'yüklendi',
  import: 'içe aktarıldı',
  export: 'dışa aktarıldı',
  send: 'gönderildi',
  resolve: 'çözüldü',
  verify: 'doğrulandı',
};

/** İşlem anahtarını doğal Türkçe etikete çevirir; bilinmeyen anahtar "Kayıt türü + fiil" ya da ham anahtar olur. */
export function auditActionLabel(action: string, entity?: string): string {
  // platform işlemleri (kimliğe bürünme, destek, abonelik…) firmanın kendi günlüğünde de okunur görünsün
  const known = AUDIT_ACTION_LABELS[action] ?? PLATFORM_AUDIT_ACTION_LABELS[action];
  if (known) return known;
  const verb = VERB_LABELS[action.split('.').at(-1) ?? ''];
  const ent = entity ? (AUDIT_ENTITY_LABELS[entity] ?? PLATFORM_AUDIT_ENTITY_LABELS[entity]) : undefined;
  if (verb && ent) return `${ent} ${verb}`;
  return action;
}

export function auditEntityLabel(entity: string): string {
  return AUDIT_ENTITY_LABELS[entity] ?? PLATFORM_AUDIT_ENTITY_LABELS[entity] ?? entity;
}
